/**
 * ConstructionOverlayRenderer — the construction-overlay VISUAL BUILDERS
 * extracted from Scene (#854, epic #828 slice Scene-S2). Scene OWNS a
 * ConstructionOverlayRenderer instance and DRIVES it by composition (it is NOT
 * a base class), mirroring the MatRenderer precedent and the ConstructionTool
 * controller pattern: the renderer holds the pure-ish item CONSTRUCTORS (the
 * `build*` / `render*` methods that turn a line / ray / group into paper
 * items), while the stateful orchestration — `render()` and the transient
 * `refresh*` lifecycle — stays on Scene and calls these builders.
 *
 * Everything the builders read from the owning Scene (the paper scope, the
 * current project, the construction-line item lookup, the live selection /
 * hover / round-mode state, the drag-distance formatter, and the rotation-
 * overlay geometry constants that stay single-definition on Scene because
 * other Scene code — tracing-image overlays, ConstructionTool hit-tests —
 * reads them too) is injected through the `ConstructionOverlayHost` interface.
 * Behaviour is identical to the in-Scene original — this is a pure move + host
 * rewiring.
 */
import paper from "paper";
import {
  descendantConstructionImageIds,
  descendantConstructionLineIds,
  isVisible,
  projectConstructionGroups,
  projectConstructionLines,
  projectTracingImages,
  type ConstructionLine,
  type Project,
} from "../model/Project";
import {
  getCornerGeometry,
  handleDistanceFromCorner,
} from "../model/constructionRounding";
import {
  getUnrotatedBounds,
  getUnrotatedGroupBounds,
} from "../model/constructionRotation";
import {
  allowsEdgeResize,
  edgeMidpoints,
  type Box,
  type ResizeCorner,
} from "../model/constructionResize";
import { offeredEdges } from "../model/transformHandles";
import { isMirrorDerived } from "../model/constructionMirror";
import { cssVar } from "../colors";
import type { GuideRay } from "./Scene";

// All construction-overlay render colors come from CSS custom properties
// defined in `src/styles.css` (per the AGENTS.md "Theming" rule). `cssVar()`
// reads the live computed value, so a future theme override flows through with
// no further changes here.
const SELECTION_STROKE = () => cssVar("--canvas-accent");
/** Selection / ray accent for marks drawn OVER a leather piece — a near-black
 *  charcoal that reads clearly on the tan leather fill / edge (#744 follow-up). */
export const LEATHER_SELECTION_STROKE = () => cssVar("--canvas-accent-leather");
const CONSTRUCTION_STROKE = () => cssVar("--construction-stroke");

/** The `data.kind` tags for one transform (rotate/resize) handle overlay,
 *  passed to `buildTransformOverlay`. Construction and leather use DISTINCT
 *  tag sets so construction's transient-rotation refresh + hit-tests never
 *  pick up leather overlay pieces and vice versa (#971). */
export interface TransformOverlayKinds {
  overlay: string;
  bbox: string;
  stem: string;
  knob: string;
  corner: string;
  edge: string;
}

/** Construction line rotate/resize overlay tags (issues #67 / #388 / #863) —
 *  unchanged strings so `transientTransformController` + `Scene` keep matching. */
const CONSTRUCTION_ROTATION_OVERLAY_KINDS: TransformOverlayKinds = {
  overlay: "line-rotation-overlay",
  bbox: "line-rotation-bbox",
  stem: "line-rotation-stem",
  knob: "line-rotation-handle",
  corner: "line-resize-handle",
  edge: "line-resize-edge-handle",
};

/** Leather object rotate/resize overlay tags (#971) — the leather
 *  `TransformController` hit-tests + refreshes against these. */
export const LEATHER_TRANSFORM_OVERLAY_KINDS: TransformOverlayKinds = {
  overlay: "leather-transform-overlay",
  bbox: "leather-transform-bbox",
  stem: "leather-transform-stem",
  knob: "leather-transform-handle",
  corner: "leather-transform-resize-handle",
  edge: "leather-transform-resize-edge-handle",
};
const CONSTRUCTION_HALO = () => cssVar("--construction-halo");

/** Distance (project mm) within which an OPEN line's endpoint counts as sitting
 *  ON another construction line — so its handle reads as a square (attached)
 *  rather than a circle (free-hanging). Snapped / on-line ends are within a hair
 *  of the line; a tenth of a mm keeps a line merely passing nearby from
 *  counting (#658). */
const END_ON_LINE_TOL = 0.1;

/** Radius (project mm) of the ring drawn around an OPEN path's END anchors
 *  (#213). The anchor dot itself is 1.1 mm, so 1.9 leaves a clear gap between
 *  the two circles at every zoom — close enough to read as one "circle within a
 *  circle" mark, far enough not to look like a fat stroke. */
const ENDPOINT_RING_RADIUS_MM = 1.9;

/** Which bbox corner the cursor is hovering just outside — drives the corner
 *  rotation-arc visual. */
type RotationCorner = "tl" | "tr" | "bl" | "br";

/** The shared Scene state + services the construction-overlay builders reach
 *  back into. The paper scope + layer stack stay owned by Scene (shared with
 *  the piece / mat / stitch renderers); the project, selection / hover / round
 *  state, the drag-distance formatter, and the rotation-overlay geometry
 *  constants stay single-definition on Scene because non-overlay code reads
 *  them too. Implemented by Scene. */
export interface ConstructionOverlayHost {
  getPaperScope(): paper.PaperScope;
  getProject(): Project | null;
  getConstructionLineItem(constructionId: string): paper.Item | null;
  /** True when handle dots will be rendered on this line — same gate the
   *  handle render loop uses. Derived (offset) lines never get handles. */
  lineHandlesVisible(line: ConstructionLine): boolean;
  /** Whether a canvas point lies over any visible leather piece — decides the
   *  ray accent. */
  isPointOverLeather(pt: { x: number; y: number }): boolean;
  formatDragDistance(mm: number): string;
  getTransientCornerHandle(): {
    constructionId: string;
    pathIndex: number;
    segmentIndex: number;
    radius: number;
    applyToAll: boolean;
  } | null;
  getRoundPresetRadius(): number | null;
  isRoundModeAll(): boolean;
  constructionRotationHandlesEnabled(): boolean;
  getLineRotationHoverCorner(): {
    constructionId: string;
    corner: RotationCorner;
  } | null;
  getGroupRotationHoverCorner(): {
    constructionGroupId: string;
    corner: RotationCorner;
  } | null;
  lineRotationHandleOffsetMm(): number;
  lineRotationHandleRadiusMm(): number;
  lineResizeHandleHalfMm(): number;
  lineRotationCornerZoneOuterMm(): number;
  lineRotationArrowArmMm(): number;
}

export class ConstructionOverlayRenderer {
  constructor(private host: ConstructionOverlayHost) {}

  /** The paper scope is owned by Scene and reached through the host; the
   *  overlay builders draw onto (and probe) the same shared scope. */
  private get scope(): paper.PaperScope {
    return this.host.getPaperScope();
  }

  /** Build a Paper.js item representing one construction (guide) line.
   *  Thin dashed yellow stroke; closed lines render as a closed
   *  polygon. Selected lines pick up the standard selection color and a
   *  slightly thicker stroke.
   *
   *  When handle dots are visible on this line (Edit sub-mode, non-
   *  derived), the visible stroke is dropped from this item — the
   *  sibling `buildConstructionLineDisplayStroke` paints a trimmed
   *  stroke with gaps at each anchor so the line visually ends at the
   *  edge of every dot rather than passing through to the centre
   *  (issue #326). The underlying geometry stays whole — hit-tests
   *  resolve through `getNearestLocation` on the path. */
  buildConstructionLineItem(
    line: ConstructionLine,
    selected: boolean,
  ): paper.Item | null {
    let path: paper.PathItem;
    try {
      path = new this.scope.CompoundPath({ pathData: line.pathData });
    } catch {
      return null;
    }
    path.data = { kind: "construction", constructionId: line.id };
    const handlesVisible = this.host.lineHandlesVisible(line);
    if (handlesVisible) {
      path.strokeColor = null;
    } else {
      path.strokeColor = new this.scope.Color(
        selected ? SELECTION_STROKE() : CONSTRUCTION_STROKE(),
      );
      path.strokeColor.alpha = selected ? 0.95 : 0.75;
    }
    path.strokeWidth = selected ? 4 : 2.4;
    path.strokeScaling = false;
    path.dashArray = [2, 1.5];
    path.fillColor = null;
    return path;
  }

  /** Issue #326 — build the visible stroke for a line whose anchor
   *  dots are showing. Each curve segment renders as its own dashed
   *  sub-path, shortened by `ANCHOR_GAP_MM` at each end so the dot
   *  that sits over the anchor reads as the visual line terminus
   *  rather than the line passing through to the centre.
   *
   *  Returns null when no curve survives trimming (every curve was
   *  shorter than `2 * ANCHOR_GAP_MM`). The geometry is purely
   *  visual — the main `buildConstructionLineItem` path keeps the
   *  full path for hit-testing.
   *
   *  Implementation note: paper.js's `Path.splitAt(offset)` mutates
   *  the source path in place, so each curve is cloned into a fresh
   *  2-segment Path first and then split twice (head-trim → mid →
   *  tail-trim) so the original construction-line item stays
   *  pristine for `getNearestLocation` callers. */
  buildConstructionLineDisplayStroke(
    line: ConstructionLine,
    selected: boolean,
  ): paper.Item | null {
    if (!this.host.lineHandlesVisible(line)) return null;
    this.scope.activate();
    let source: paper.CompoundPath;
    try {
      source = new this.scope.CompoundPath({
        pathData: line.pathData,
        insert: false,
      });
    } catch {
      return null;
    }
    // Match the anchor dot's geometric radius (1.1 mm — see
    // `buildConstructionHandlesFromPath`) so the trimmed stroke
    // visually meets the dot at its edge instead of stopping
    // half a millimetre short. The dot's stroke extends outward
    // from the radius by `strokeWidth/2` px, which at typical zoom
    // covers the few pixels between the line's end and the visible
    // dot outline.
    const ANCHOR_GAP_MM = 1.1;
    const subpaths: paper.Path[] =
      source.children && source.children.length > 0
        ? (source.children as paper.Path[])
        : [source as unknown as paper.Path];
    const group = new this.scope.Group();
    group.data = {
      ephemeral: true,
      kind: "construction-display-stroke",
      constructionId: line.id,
    };
    const strokeColor = new this.scope.Color(
      selected ? SELECTION_STROKE() : CONSTRUCTION_STROKE(),
    );
    strokeColor.alpha = selected ? 0.95 : 0.75;
    for (const sub of subpaths) {
      for (const curve of sub.curves) {
        const length = curve.length;
        if (length <= 2 * ANCHOR_GAP_MM) continue;
        // Reconstruct the curve as a stand-alone 2-segment Path so
        // `splitAt` can chop the ends without touching the source.
        const startSeg = new this.scope.Segment(
          curve.segment1.point.clone(),
          undefined,
          curve.segment1.handleOut.clone(),
        );
        const endSeg = new this.scope.Segment(
          curve.segment2.point.clone(),
          curve.segment2.handleIn.clone(),
          undefined,
        );
        const piece = new this.scope.Path({
          segments: [startSeg, endSeg],
          insert: false,
        });
        // Trim ANCHOR_GAP_MM off the start — `splitAt` returns the
        // tail portion and leaves `piece` as the discarded head.
        const tail = piece.splitAt(ANCHOR_GAP_MM);
        piece.remove();
        if (!tail) continue;
        // Trim ANCHOR_GAP_MM off the new end.
        const tailLen = tail.length;
        if (tailLen <= ANCHOR_GAP_MM) {
          tail.remove();
          continue;
        }
        const discard = tail.splitAt(tailLen - ANCHOR_GAP_MM);
        if (discard) discard.remove();
        tail.strokeColor = strokeColor;
        tail.strokeWidth = selected ? 4 : 2.4;
        tail.strokeScaling = false;
        tail.dashArray = [2, 1.5];
        tail.fillColor = null;
        tail.data = { ephemeral: true };
        group.addChild(tail);
      }
    }
    source.remove();
    if (!group.children || group.children.length === 0) {
      group.remove();
      return null;
    }
    return group;
  }

  /** Build a dark halo path that sits beneath the yellow construction
   *  stroke to keep it readable against light theme canvas and the
   *  warm leather fill — see CONSTRUCTION_HALO() for the rationale.
   *  Matches the main stroke's dash pattern so each dash gets a thin
   *  dark outline rather than a continuous backing (continuous would
   *  paint through the gaps and visually merge the dashes). Marked
   *  ephemeral so hit-tests pass through to the yellow path / handles
   *  sitting on top. */
  buildConstructionLineHalo(
    line: ConstructionLine,
    selected: boolean,
  ): paper.Item | null {
    let path: paper.PathItem;
    try {
      path = new this.scope.CompoundPath({ pathData: line.pathData });
    } catch {
      return null;
    }
    // Tag with the owning line id so per-frame drag code can find
    // and translate this halo in lock-step with the yellow stroke
    // — without the id, the halo stays put during a drag and only
    // snaps to the new position on the next render (issue #85).
    path.data = {
      kind: "construction-halo",
      constructionId: line.id,
      ephemeral: true,
    };
    // When the line's handle dots are showing (Edit mode, non-
    // derived), the visible halo migrates to the sibling
    // `construction-halo-display` group so its stroke can be
    // trimmed at every anchor (issue #326). This Path stays in the
    // tree so per-frame translate / refresh code that walks the
    // construction-group can still find it; the strokeless body
    // just doesn't render anything.
    const handlesVisible = this.host.lineHandlesVisible(line);
    if (handlesVisible) {
      path.strokeColor = null;
    } else {
      path.strokeColor = new this.scope.Color(CONSTRUCTION_HALO());
      path.strokeColor.alpha = 0.6;
    }
    // ~0.8 mm overhang on each side of the yellow stroke is enough to
    // read as an outline at typical zoom levels without thickening the
    // overall line visually.
    path.strokeWidth = (selected ? 4 : 2.4) + 1.6;
    path.strokeScaling = false;
    path.dashArray = [2, 1.5];
    path.fillColor = null;
    return path;
  }

  /** Issue #326 — trimmed visible halo. Mirrors
   *  `buildConstructionLineDisplayStroke` but uses the halo's
   *  thicker stroke + halo colour. Tagged
   *  `construction-halo-display` so the per-frame refresh code can
   *  find and rebuild it alongside the yellow display stroke. */
  buildConstructionLineHaloDisplay(
    line: ConstructionLine,
    selected: boolean,
  ): paper.Item | null {
    if (!this.host.lineHandlesVisible(line)) return null;
    this.scope.activate();
    let source: paper.CompoundPath;
    try {
      source = new this.scope.CompoundPath({
        pathData: line.pathData,
        insert: false,
      });
    } catch {
      return null;
    }
    const ANCHOR_GAP_MM = 1.1;
    const subpaths: paper.Path[] =
      source.children && source.children.length > 0
        ? (source.children as paper.Path[])
        : [source as unknown as paper.Path];
    const group = new this.scope.Group();
    group.data = {
      ephemeral: true,
      kind: "construction-halo-display",
      constructionId: line.id,
    };
    const haloColor = new this.scope.Color(CONSTRUCTION_HALO());
    haloColor.alpha = 0.6;
    for (const sub of subpaths) {
      for (const curve of sub.curves) {
        const length = curve.length;
        if (length <= 2 * ANCHOR_GAP_MM) continue;
        const startSeg = new this.scope.Segment(
          curve.segment1.point.clone(),
          undefined,
          curve.segment1.handleOut.clone(),
        );
        const endSeg = new this.scope.Segment(
          curve.segment2.point.clone(),
          curve.segment2.handleIn.clone(),
          undefined,
        );
        const piece = new this.scope.Path({
          segments: [startSeg, endSeg],
          insert: false,
        });
        const tail = piece.splitAt(ANCHOR_GAP_MM);
        piece.remove();
        if (!tail) continue;
        const tailLen = tail.length;
        if (tailLen <= ANCHOR_GAP_MM) {
          tail.remove();
          continue;
        }
        const discard = tail.splitAt(tailLen - ANCHOR_GAP_MM);
        if (discard) discard.remove();
        tail.strokeColor = haloColor;
        tail.strokeWidth = (selected ? 4 : 2.4) + 1.6;
        tail.strokeScaling = false;
        tail.dashArray = [2, 1.5];
        tail.fillColor = null;
        tail.data = { ephemeral: true };
        group.addChild(tail);
      }
    }
    source.remove();
    if (!group.children || group.children.length === 0) {
      group.remove();
      return null;
    }
    return group;
  }

  /** Build the per-anchor handles for one construction line. Returns a
   *  group with one filled circle per segment, each tagged with the
   *  segment's `(pathIndex, segmentIndex)` so the tool's drag code can
   *  find the matching segment in the parsed path on commit. */
  buildConstructionHandles(line: ConstructionLine): paper.Item | null {
    let path: paper.CompoundPath;
    try {
      path = new this.scope.CompoundPath({
        pathData: line.pathData,
        insert: false,
      });
    } catch {
      return null;
    }
    const group = this.buildConstructionHandlesFromPath(
      path,
      line.id,
      buildAnchorLockMap(line),
    );
    path.remove();
    return group;
  }

  buildConstructionHandlesFromPath(
    path: paper.CompoundPath,
    constructionId: string,
    lockMap: Map<string, "angle" | "smooth">,
    /** Stroke color for every handle mark. Defaults to the
     *  construction yellow; the leather Edit-points overlay passes
     *  the leather selection accent so anchors / tangents read
     *  against the tan leather instead of vanishing (#970 fix). */
    overlayColor: string = CONSTRUCTION_STROKE(),
  ): paper.Item | null {
    const group = new this.scope.Group();
    group.data = { kind: "construction-handles", constructionId };
    const subpaths: paper.Path[] =
      path.children && path.children.length > 0
        ? (path.children as paper.Path[])
        : [path as unknown as paper.Path];
    // Geometry of every OTHER visible construction line — used to mark which of
    // this line's OPEN endpoints sit ON another line (#658). Removed at the end.
    const otherPaths = this.buildOtherConstructionPaths(constructionId);
    // Three-phase render so the final z-order (bottom → top) is:
    //   tangent lines  →  anchor dots  →  bezier tip dots
    //
    // Tip dots on top so the user can SEE a short tangent's
    // endpoint even when it lands inside the anchor's visible
    // circle (issue #329). Matches the click priority in
    // `ConstructionTool.findVisibleHandleHit`, which now prefers
    // tips over anchors when both contain the cursor.
    subpaths.forEach((sub, pathIndex) => {
      sub.segments.forEach((seg, segmentIndex) => {
        const lockMode = lockMap.get(`${pathIndex}:${segmentIndex}`) ?? null;
        if (!seg.handleIn.isZero()) {
          this.appendBezierTangentLine(
            group,
            seg.point,
            seg.point.add(seg.handleIn),
            {
              constructionId,
              pathIndex,
              segmentIndex,
              side: "in",
              lockMode,
            },
            overlayColor,
          );
        }
        if (!seg.handleOut.isZero()) {
          this.appendBezierTangentLine(
            group,
            seg.point,
            seg.point.add(seg.handleOut),
            {
              constructionId,
              pathIndex,
              segmentIndex,
              side: "out",
              lockMode,
            },
            overlayColor,
          );
        }
      });
    });
    subpaths.forEach((sub, pathIndex) => {
      sub.segments.forEach((seg, segmentIndex) => {
        // Anchor fill follows "does this anchor have any bezier
        // curve at all" rather than the lock mode — sharp corners
        // (no handles) read as hollow, every curved anchor reads as
        // filled, regardless of independent / angle / smooth lock.
        const hasCurve = !seg.handleIn.isZero() || !seg.handleOut.isZero();
        // #658: draw ANY anchor that sits ON another construction line as a
        // SQUARE (attached / a junction), and one that doesn't as a CIRCLE
        // (free) — so free ends and junctions are both easy to read while
        // closing shapes. Applies to every vertex, not just line ends.
        const onOtherLine = this.pointOnAnyPath(
          seg.point,
          otherPaths,
          END_ON_LINE_TOL,
        );
        // Anchor dots render slightly larger than the 0.7 mm tangent dots so
        // they read heavier even when hollow — anchors are the more "important"
        // interaction target. The square matches that visual size.
        const dot = onOtherLine
          ? new this.scope.Path.Rectangle(
              new this.scope.Point(seg.point.x - 1.1, seg.point.y - 1.1),
              new this.scope.Size(2.2, 2.2),
            )
          : new this.scope.Path.Circle(seg.point, 1.1);
        if (hasCurve) {
          // Filled anchor — paint at 75% opacity (issue #325 follow-
          // up) so the centre dot rendered BELOW remains visible
          // through the fill instead of being painted over.
          const filledColor = new this.scope.Color(overlayColor);
          filledColor.alpha = 0.75;
          dot.fillColor = filledColor;
        } else {
          dot.fillColor = new this.scope.Color(0, 0, 0, 0);
        }
        dot.strokeColor = new this.scope.Color(overlayColor);
        dot.strokeWidth = 2.0;
        dot.strokeScaling = false;
        dot.data = {
          kind: "construction-handle",
          constructionId,
          pathIndex,
          segmentIndex,
          pointOnLine: onOtherLine,
        };
        // Centre dot (issue #325 follow-up) — a small filled marker
        // at the exact anchor position. Sits BENEATH the visible
        // outer ring so the 75%-opacity fill on curved anchors lets
        // it show through; on hollow anchors the transparent fill
        // makes it visible directly.
        const anchorCentre = new this.scope.Path.Circle(seg.point, 0.25);
        anchorCentre.fillColor = new this.scope.Color(overlayColor);
        anchorCentre.strokeColor = null;
        anchorCentre.data = {
          kind: "construction-handle",
          constructionId,
          pathIndex,
          segmentIndex,
        };
        group.addChild(anchorCentre);
        group.addChild(dot);
        // #213: an END of an open path gets a second ring around the
        // dot. Splitting a line drops two endpoints exactly where one
        // anchor used to be, and without this they read as an ordinary
        // point on the line. The ring is a THIRD axis, deliberately
        // independent of the two already in use — fill still means
        // "has bezier handles" (#325) and the square still means
        // "sits on another line" (#658) — so a split end that also
        // touches its other half reads as both at once.
        const isEnd =
          !sub.closed &&
          (segmentIndex === 0 || segmentIndex === sub.segments.length - 1);
        if (isEnd) {
          const ring = new this.scope.Path.Circle(
            seg.point,
            ENDPOINT_RING_RADIUS_MM,
          );
          ring.fillColor = null;
          ring.strokeColor = new this.scope.Color(overlayColor);
          ring.strokeWidth = 1.2;
          ring.strokeScaling = false;
          // Ephemeral so `Scene.hitTest` skips it: the ring is wider
          // than the anchor it decorates, and picking it would let a
          // click land on an endpoint from further away than the dot
          // the user is aiming at. `findVisibleHandleHit` measures
          // geometry rather than rendered items, so its own targeting
          // is unaffected either way.
          ring.data = {
            kind: "construction-handle-endpoint-ring",
            constructionId,
            pathIndex,
            segmentIndex,
            ephemeral: true,
          };
          group.addChild(ring);
        }
      });
    });
    subpaths.forEach((sub, pathIndex) => {
      sub.segments.forEach((seg, segmentIndex) => {
        const lockMode = lockMap.get(`${pathIndex}:${segmentIndex}`) ?? null;
        if (!seg.handleIn.isZero()) {
          this.appendBezierTipDot(
            group,
            seg.point.add(seg.handleIn),
            {
              constructionId,
              pathIndex,
              segmentIndex,
              side: "in",
              lockMode,
            },
            overlayColor,
          );
        }
        if (!seg.handleOut.isZero()) {
          this.appendBezierTipDot(
            group,
            seg.point.add(seg.handleOut),
            {
              constructionId,
              pathIndex,
              segmentIndex,
              side: "out",
              lockMode,
            },
            overlayColor,
          );
        }
      });
    });
    for (const op of otherPaths) op.remove();
    return group;
  }

  /** Uninserted geometry of every visible construction line EXCEPT `exceptId` —
   *  used to test which of a line's endpoints sit on another line (#658). The
   *  caller must `.remove()` each when done. */
  private buildOtherConstructionPaths(exceptId: string): paper.PathItem[] {
    const out: paper.PathItem[] = [];
    const project = this.host.getProject();
    const lines = project ? projectConstructionLines(project) : [];
    for (const l of lines) {
      if (l.id === exceptId || !isVisible(l)) continue;
      try {
        out.push(
          new this.scope.CompoundPath({ pathData: l.pathData, insert: false }),
        );
      } catch {
        /* skip un-parseable geometry */
      }
    }
    return out;
  }

  /** Whether `pt` lies within `tol` (project mm) of any of `paths`. */
  private pointOnAnyPath(
    pt: paper.Point,
    paths: paper.PathItem[],
    tol: number,
  ): boolean {
    for (const p of paths) {
      const loc = p.getNearestLocation(pt);
      if (loc && loc.point.getDistance(pt) <= tol) return true;
    }
    return false;
  }

  /** Add the trimmed tangent stroke from anchor to tip — bottom of
   *  the three-phase handle render. The tangent line carries the
   *  same `bezier-handle` data tag as the tip dot so a tap anywhere
   *  along the line (not just on the dot) still resolves as a
   *  bezier-handle hit. Thin (0.4 px) when independent / no lock,
   *  thick (2 px) when angle-locked or smooth-locked. */
  private appendBezierTangentLine(
    group: paper.Group,
    anchor: paper.Point,
    handlePoint: paper.Point,
    tag: {
      constructionId: string;
      pathIndex: number;
      segmentIndex: number;
      side: "in" | "out";
      /** Lock mode at this anchor. Drives the tangent line thickness
       *  via the same two-axis encoding the tip dot uses:
       *    - line thick  → angles locked (angle OR smooth)
       *  Independent → thin, angle / smooth → thick. */
      lockMode: "angle" | "smooth" | null;
    },
    overlayColor: string = CONSTRUCTION_STROKE(),
  ) {
    // Trim the tangent stroke so it visually starts at the edge of
    // the anchor dot and ends at the edge of the tip dot rather than
    // passing through the centre of either (issue #326). Anchor dot
    // radius is 1.1 mm; tip dot radius is 0.7 mm. Buffer slightly
    // for the stroke widths so the visible stroke clears each dot's
    // outer edge by a hair. Falls back to the full anchor → tip
    // segment when the handle vector is shorter than the combined
    // trim, where the tangent line would otherwise reverse.
    const ANCHOR_TRIM_MM = 1.5;
    const TIP_TRIM_MM = 1.0;
    const direction = handlePoint.subtract(anchor);
    const distance = direction.length;
    let lineStart = anchor;
    let lineEnd = handlePoint;
    if (distance > ANCHOR_TRIM_MM + TIP_TRIM_MM) {
      const unit = direction.divide(distance);
      lineStart = anchor.add(unit.multiply(ANCHOR_TRIM_MM));
      lineEnd = handlePoint.subtract(unit.multiply(TIP_TRIM_MM));
    }
    const line = new this.scope.Path({
      segments: [lineStart, lineEnd],
    });
    line.strokeColor = new this.scope.Color(overlayColor);
    line.strokeColor.alpha = 0.6;
    line.strokeWidth = tag.lockMode !== null ? 2.0 : 0.4;
    line.strokeScaling = false;
    line.fillColor = null;
    line.data = {
      kind: "bezier-handle",
      constructionId: tag.constructionId,
      pathIndex: tag.pathIndex,
      segmentIndex: tag.segmentIndex,
      side: tag.side,
    };
    group.addChild(line);
  }

  /** Add the visible tip dot at a bezier handle's endpoint — top of
   *  the three-phase handle render so a short handle's tip dot
   *  remains visible even when it sits inside the anchor's outer
   *  ring (issue #329). Hollow when independent or angle-locked,
   *  filled when smooth-locked (length-locked too). */
  private appendBezierTipDot(
    group: paper.Group,
    handlePoint: paper.Point,
    tag: {
      constructionId: string;
      pathIndex: number;
      segmentIndex: number;
      side: "in" | "out";
      lockMode: "angle" | "smooth" | null;
    },
    overlayColor: string = CONSTRUCTION_STROKE(),
  ) {
    const dot = new this.scope.Path.Circle(handlePoint, 0.7);
    dot.fillColor =
      tag.lockMode === "smooth"
        ? new this.scope.Color(overlayColor)
        : new this.scope.Color(0, 0, 0, 0);
    dot.strokeColor = new this.scope.Color(overlayColor);
    dot.strokeWidth = 0.5;
    dot.strokeScaling = false;
    dot.data = {
      kind: "bezier-handle",
      constructionId: tag.constructionId,
      pathIndex: tag.pathIndex,
      segmentIndex: tag.segmentIndex,
      side: tag.side,
    };
    group.addChild(dot);
  }

  /** Build the round-eligible corner handles for one construction
   *  line (issue #100). For each qualifying sharp corner, the
   *  handle sits AT THE ARC CENTRE along the corner's interior
   *  bisector — so the user reads "this dot is the centre of the
   *  arc that would be inscribed" rather than "this dot is the
   *  corner." Distance from the corner is `r / sin(halfAngle)`,
   *  where `r` is either (a) the corner's existing rounding entry,
   *  (b) the panel's currently-locked preset radius, or (c) a small
   *  default. A faint dashed spoke from the corner to the handle
   *  makes the centre-of-arc relationship explicit. The Round
   *  sub-mode's hit-test in `ConstructionTool` recomputes the same
   *  handle position to consume clicks; these items are visual cues
   *  only. */
  buildCornerHandles(line: ConstructionLine): paper.Item | null {
    const sharpData = line.cornerRounding?.sharpPathData ?? line.pathData;
    if (!sharpData) return null;
    let cp: paper.CompoundPath;
    try {
      cp = new this.scope.CompoundPath({
        pathData: sharpData,
        insert: false,
      });
    } catch {
      return null;
    }
    const group = new this.scope.Group();
    group.data = {
      kind: "construction-corner-handles",
      constructionId: line.id,
    };
    const subpaths: paper.Path[] =
      cp.children && cp.children.length > 0
        ? (cp.children as paper.Path[])
        : [cp as unknown as paper.Path];
    const radiusByKey = new Map<string, number>();
    for (const c of line.cornerRounding?.corners ?? []) {
      radiusByKey.set(`${c.pathIndex}:${c.segmentIndex}`, c.radius);
    }
    const zoom = this.scope.view?.zoom ?? 1;
    // Pre-zoom screen → project radius for the handle; without
    // strokeScaling the visible size stays constant regardless of
    // zoom. ~3.2 px outer radius is small enough to coexist with the
    // construction stroke without obscuring nearby geometry.
    const handleRadius = 3.2 / zoom;
    const th = this.host.getTransientCornerHandle();
    const transient = th && th.constructionId === line.id ? th : null;
    const transientKey = transient
      ? `${transient.pathIndex}:${transient.segmentIndex}`
      : null;
    const roundPresetRadius = this.host.getRoundPresetRadius();
    const roundModeAll = this.host.isRoundModeAll();
    for (let pi = 0; pi < subpaths.length; pi++) {
      const sub = subpaths[pi]!;
      for (let si = 0; si < sub.segments.length; si++) {
        const geom = getCornerGeometry(sharpData, pi, si, this.scope);
        if (!geom) continue;
        if (geom.maxRadius < 0.5) continue;
        const key = `${pi}:${si}`;
        const existing = radiusByKey.get(key);
        const isRounded = existing !== undefined;
        // Display radius hierarchy (high → low): an in-flight drag
        // override beats everything (so the dot follows the cursor
        // per-frame); an actual rounding entry beats the panel's
        // preset (we don't shift a committed corner's dot when the
        // user types a different preset value); the preset beats
        // the "no rounding yet" default of 0. Preset is suppressed
        // when it exceeds the corner's `maxRadius` since that radius
        // can never be committed here (issue #100 follow-up) — the
        // knob stays at its rest position to read as "this corner
        // won't take that preset."
        const presetFits =
          roundPresetRadius !== null &&
          isFinite(roundPresetRadius) &&
          roundPresetRadius > 0 &&
          roundPresetRadius <= geom.maxRadius;
        const baseRadius = existing ?? (presetFits ? roundPresetRadius! : 0);
        // Transient override applies to the targeted corner always,
        // and to ALL eligible corners on the line when the drag is
        // in linked (all-corners) mode — that's how every handle
        // dot moves together when the user drags one in linked
        // mode.
        const transientApplies =
          transient !== null && (transient.applyToAll || transientKey === key);
        const displayRadius = transientApplies
          ? Math.min(Math.max(0, transient!.radius), geom.maxRadius)
          : Math.min(baseRadius, geom.maxRadius);
        const handleDist = handleDistanceFromCorner(geom, displayRadius);
        const handleX = geom.cornerPoint.x + geom.bisectorDir.x * handleDist;
        const handleY = geom.cornerPoint.y + geom.bisectorDir.y * handleDist;
        const handlePt = new this.scope.Point(handleX, handleY);
        // Spoke: a faint dashed line from the corner to the handle
        // so the user can see "the handle is the centre of an arc
        // tangent to the two sides starting from the corner." Drawn
        // first so the handle dot sits on top.
        const spoke = new this.scope.Path.Line(
          new this.scope.Point(geom.cornerPoint.x, geom.cornerPoint.y),
          handlePt,
        );
        spoke.strokeColor = new this.scope.Color(CONSTRUCTION_STROKE());
        spoke.strokeColor.alpha = 0.45;
        spoke.strokeWidth = 1;
        spoke.strokeScaling = false;
        spoke.dashArray = [2, 2];
        spoke.fillColor = null;
        spoke.data = { kind: "construction-corner-handle-spoke" };
        group.addChild(spoke);
        // Two visual axes — fill = "this corner carries a
        // committed rounding entry" (the radius has been modified),
        // stroke = "linked" (a drag on any handle will move every
        // corner together). The two are independent and stack:
        //  • hollow + single stroke  = un-rounded, single-corner mode
        //  • filled + single stroke  = rounded, single-corner mode
        //  • hollow + outer ring     = un-rounded, all-corners linked
        //  • filled + outer ring     = rounded, all-corners linked
        // The outer ring is a separate concentric circle drawn at
        // 1.7× the dot radius so the doubled outline reads as a
        // chain link, distinct from the fill state. Same yellow
        // colour so it stays grouped with the handle visually.
        if (roundModeAll) {
          const outerRingRadius = handleRadius * 1.7;
          const outerRing = new this.scope.Path.Circle(
            handlePt,
            outerRingRadius,
          );
          outerRing.strokeColor = new this.scope.Color(CONSTRUCTION_STROKE());
          outerRing.strokeWidth = 1.2;
          outerRing.strokeScaling = false;
          outerRing.fillColor = null;
          outerRing.data = { kind: "construction-corner-handle-link" };
          group.addChild(outerRing);
        }
        const dot = new this.scope.Path.Circle(handlePt, handleRadius);
        dot.strokeColor = new this.scope.Color(CONSTRUCTION_STROKE());
        dot.strokeWidth = 1.6;
        dot.strokeScaling = false;
        dot.fillColor = isRounded
          ? new this.scope.Color(CONSTRUCTION_STROKE())
          : null;
        dot.data = {
          kind: "construction-corner-handle",
          constructionId: line.id,
          pathIndex: pi,
          segmentIndex: si,
        };
        group.addChild(dot);
      }
    }
    cp.remove();
    return group;
  }

  /** Build a dashed bbox + top-handle (line + circle) + optional
   *  hover-driven quarter-arc for one selected construction line.
   *  The bbox is the line's *un-rotated* bbox (via getUnrotatedBounds)
   *  drawn rotated by `line.rotation` about its centre — so each
   *  rotation step rotates the original frame rather than spawning
   *  a fresh axis-aligned one (issue #67). The top knob carries
   *  `kind: "line-rotation-handle"` for hit-testing; the rest is
   *  ephemeral. Tagged with the owning constructionId so the
   *  transient-rotation refresh can rotate every overlay piece in
   *  lock-step with the line. */
  buildLineRotationOverlay(line: ConstructionLine): paper.Item | null {
    const b = getUnrotatedBounds(line, this.scope);
    // A height-0 bbox is legitimate for an endpoint-based `line`
    // primitive (its natural form is a horizontal segment of
    // length × 0). The dashed frame still draws as a thin tilted
    // stroke that lays right over the construction line, and the
    // stem + knob extend perpendicular to it — same rotation
    // affordance every other shape gets (issue #360).
    if (!b || b.width <= 0 || b.height < 0) return null;
    const hover = this.host.getLineRotationHoverCorner();
    return this.buildTransformOverlay({
      box: { x: b.x, y: b.y, width: b.width, height: b.height },
      center: { x: b.cx, y: b.cy },
      rotationDeg: line.rotation ?? 0,
      allowEdge: allowsEdgeResize(line.primitive?.kind),
      // A mirror keeps its rotation knob — that gesture routes to the source
      // — but has no resize squares: the deriver rebuilds it from the source
      // on commit, so a resize would silently undo itself (#1167).
      allowResize: !isMirrorDerived(line),
      stroke: SELECTION_STROKE(),
      kinds: CONSTRUCTION_ROTATION_OVERLAY_KINDS,
      data: { constructionId: line.id },
      hoverArc:
        hover && hover.constructionId === line.id
          ? { corner: hover.corner, tagId: line.id }
          : null,
    });
  }

  /**
   * The shared rotate/resize handle overlay (#971): a dashed bbox frame + a
   * top rotation stem/knob + corner (and optionally edge) resize squares +
   * an optional corner hover arc, drawn in the un-rotated frame and tilted by
   * `rotationDeg` about `center`. Parameterized by an explicit `box` / stroke /
   * kind-tags / merged `data`, so BOTH construction lines
   * (`buildLineRotationOverlay`, tilt-carrying, accent stroke) and leather
   * objects (the leather TransformController, no tilt, leather-accent stroke,
   * distinct kind-tags so construction's hit-tests / transient-rotation refresh
   * never pick them up) render from one place. Handle sizes are the shared
   * screen-constant `host.line*Mm()` values divided by zoom (#864).
   */
  buildTransformOverlay(opts: {
    box: Box;
    center: { x: number; y: number };
    rotationDeg: number;
    allowEdge: boolean;
    /** False drops every corner and edge square, leaving the frame + knob.
     *  Defaults to true. */
    allowResize?: boolean;
    stroke: string;
    kinds: TransformOverlayKinds;
    /** Merged into every piece's `data` (e.g. `{ constructionId }` or
     *  `{ transformOwner }`) so the owner can find + refresh its pieces. */
    data: Record<string, unknown>;
    hoverArc?: { corner: ResizeCorner; tagId: string } | null;
  }): paper.Item | null {
    const { box, kinds, stroke, data } = opts;
    if (box.width <= 0 || box.height < 0) return null;
    const center = new this.scope.Point(opts.center.x, opts.center.y);
    // Screen-constant affordances (#864): mm are "screen px at zoom 1", so
    // dividing by live zoom keeps on-screen size stable at every zoom.
    const zoom = this.scope.view?.zoom ?? 1;
    const color = () => new this.scope.Color(stroke);

    const frame = new this.scope.Path.Rectangle({
      rectangle: new this.scope.Rectangle(box.x, box.y, box.width, box.height),
      strokeColor: color(),
      strokeWidth: 1,
      strokeScaling: false,
      dashArray: [4, 3],
      fillColor: null,
    });
    frame.data = { kind: kinds.bbox, ...data, ephemeral: true };

    // Top handle: vertical stem out of the top-centre, capped by a filled
    // circle, drawn in the un-rotated frame and rotated below.
    const topX = box.x + box.width / 2;
    const topY = box.y;
    // Screen-constant stem offset (#971 follow-up): like the knob radius, the
    // gap between the bbox top and the knob is "screen px at zoom 1" divided by
    // the live zoom, so the handle sits the same distance away at every zoom.
    const stemEnd = topY - this.host.lineRotationHandleOffsetMm() / zoom;
    const stem = new this.scope.Path.Line(
      new this.scope.Point(topX, topY),
      new this.scope.Point(topX, stemEnd),
    );
    stem.strokeColor = color();
    stem.strokeWidth = 1;
    stem.strokeScaling = false;
    stem.data = { kind: kinds.stem, ...data, ephemeral: true };
    const knob = new this.scope.Path.Circle(
      new this.scope.Point(topX, stemEnd),
      this.host.lineRotationHandleRadiusMm() / zoom,
    );
    knob.fillColor = color();
    knob.strokeColor = null;
    knob.strokeScaling = false;
    // Ephemeral so paper's stroke-tolerance hit-test skips the knob — the
    // owning tool's tight geometric check is the only allowed entry point.
    knob.data = { kind: kinds.knob, ...data, ephemeral: true };

    const overlay = new this.scope.Group([frame, stem, knob]);
    overlay.data = { kind: kinds.overlay, ...data };

    // Corner resize squares at each bbox corner, drawn in the un-rotated frame
    // and tilted with the overlay below.
    //
    // Qualifying on EITHER axis rather than both (#1055): a straight seam is
    // 1-D, and requiring both dimensions gave it no resize affordance at all —
    // just the rotation knob — even though lengthening it is the commonest
    // thing anyone wants to do to a seam. Its four corners collapse onto its
    // two endpoints, which is exactly where a maker reaches to stretch it.
    // A true point (both axes flat) still qualifies for nothing.
    if (opts.allowResize !== false && (box.width > 1e-3 || box.height > 1e-3)) {
      const half = this.host.lineResizeHandleHalfMm() / zoom;
      const addHandle = (hx: number, hy: number, kind: string) => {
        const sq = new this.scope.Path.Rectangle({
          rectangle: new this.scope.Rectangle(
            hx - half,
            hy - half,
            half * 2,
            half * 2,
          ),
          fillColor: color(),
          strokeColor: null,
        });
        sq.strokeScaling = false;
        // Ephemeral so paper's hit-test ignores it — the owning tool's
        // geometric check is the only entry point, mirroring the knob.
        sq.data = { kind, ...data, ephemeral: true };
        overlay.addChild(sq);
      };
      for (const [hx, hy] of [
        [box.x, box.y],
        [box.x + box.width, box.y],
        [box.x, box.y + box.height],
        [box.x + box.width, box.y + box.height],
      ] as const) {
        addHandle(hx, hy, kinds.corner);
      }
      if (opts.allowEdge) {
        const mids = edgeMidpoints(box);
        // The SAME set `pickResizeHandle` will accept (#1055): an axis with no
        // extent has no scale to change, so drawing its pair would put a
        // grabbable square on the canvas that provably does nothing.
        for (const e of offeredEdges(box)) {
          addHandle(mids[e].x, mids[e].y, kinds.edge);
        }
      }
    }

    if (opts.hoverArc) {
      const arc = this.buildRotationCornerArc(
        new this.scope.Rectangle(box.x, box.y, box.width, box.height),
        opts.hoverArc.corner,
        opts.hoverArc.tagId,
      );
      if (arc) overlay.addChild(arc);
    }

    if (Math.abs(opts.rotationDeg) > 1e-6) {
      overlay.rotate(opts.rotationDeg, center);
    }
    return overlay;
  }

  /** Quarter-circle arc with tangent-aligned arrowhead chevrons just
   *  outside the given corner of `bounds`. The chevron tips sit one
   *  arrow-length PAST the arc endpoint along the local tangent so
   *  the arrows read as "the arc continues outward" rather than
   *  "pointing at the endpoint" (issue #67). */
  buildRotationCornerArc(
    bounds: paper.Rectangle,
    corner: "tl" | "tr" | "bl" | "br",
    constructionId: string,
  ): paper.Item | null {
    // Screen-constant arc + arrowheads (#864): the zone radius and arm
    // length are "screen px at zoom 1", divided by the live zoom so the
    // rotation indicator holds its on-screen size across zoom levels.
    const zoom = this.scope.view?.zoom ?? 1;
    const r = (this.host.lineRotationCornerZoneOuterMm() * 0.6) / zoom;
    let cx: number;
    let cy: number;
    let startAngle: number;
    switch (corner) {
      case "tl":
        cx = bounds.x;
        cy = bounds.y;
        startAngle = 180;
        break;
      case "tr":
        cx = bounds.x + bounds.width;
        cy = bounds.y;
        startAngle = 270;
        break;
      case "bl":
        cx = bounds.x;
        cy = bounds.y + bounds.height;
        startAngle = 90;
        break;
      case "br":
        cx = bounds.x + bounds.width;
        cy = bounds.y + bounds.height;
        startAngle = 0;
        break;
    }
    // 117° (90° × 1.30) — slightly wider than the strict quadrant
    // so the arc spills past the bbox-edge tangents and reads as a
    // clearer rotation indicator. The midpoint stays on the outward
    // diagonal — extra sweep is split evenly on both sides.
    const sweep = 117;
    const extra = (sweep - 90) / 2;
    startAngle -= extra;
    const endAngle = startAngle + sweep;
    const toPoint = (deg: number) => {
      const rad = (deg * Math.PI) / 180;
      return new this.scope.Point(
        cx + r * Math.cos(rad),
        cy + r * Math.sin(rad),
      );
    };
    const midPoint = toPoint(startAngle + sweep / 2);
    const startPt = toPoint(startAngle);
    const endPt = toPoint(endAngle);
    const arc = new this.scope.Path.Arc(startPt, midPoint, endPt);
    arc.strokeColor = new this.scope.Color(SELECTION_STROKE());
    arc.strokeWidth = 1.8;
    arc.strokeScaling = false;
    arc.fillColor = null;
    arc.data = {
      kind: "line-rotation-arc",
      constructionId,
      ephemeral: true,
    };

    /** V-shaped arrowhead with its vertex EXACTLY on the arc end —
     *  the two arms meet there and splay INWARD at ±30° from the
     *  inward tangent (60° total spread). Matches the anchor-point
     *  hint's arrow geometry so all rotation indicators share one
     *  visual idiom. */
    const arrow = (atDeg: number, outwardSign: 1 | -1) => {
      const rad = (atDeg * Math.PI) / 180;
      const tx = -Math.sin(rad);
      const ty = Math.cos(rad);
      const inX = -outwardSign * tx;
      const inY = -outwardSign * ty;
      const at = toPoint(atDeg);
      const armLength = this.host.lineRotationArrowArmMm() / zoom;
      const c30 = Math.cos((30 * Math.PI) / 180);
      const s30 = Math.sin((30 * Math.PI) / 180);
      const a1 = new this.scope.Point(
        at.x + (inX * c30 - inY * s30) * armLength,
        at.y + (inX * s30 + inY * c30) * armLength,
      );
      const a2 = new this.scope.Point(
        at.x + (inX * c30 + inY * s30) * armLength,
        at.y + (-inX * s30 + inY * c30) * armLength,
      );
      const chevron = new this.scope.Path([a1, at, a2]);
      chevron.strokeColor = new this.scope.Color(SELECTION_STROKE());
      chevron.strokeWidth = 1.8;
      chevron.strokeScaling = false;
      chevron.fillColor = null;
      chevron.data = {
        kind: "line-rotation-arrow",
        constructionId,
        ephemeral: true,
      };
      return chevron;
    };

    const group = new this.scope.Group([
      arc,
      arrow(startAngle, -1),
      arrow(endAngle, 1),
    ]);
    group.data = {
      kind: "line-rotation-arc-group",
      constructionId,
      ephemeral: true,
    };
    return group;
  }

  /** Build a yellow dashed bounding-box (with the same dark halo
   *  beneath as construction lines carry) for one selected
   *  construction group. Bounds are exactly the union of every
   *  descendant line's rendered bounds — no outset, the frame hugs
   *  the actual stroke envelope. Skipped when the group has no
   *  rendered members yet — caller treats `null` as "no overlay for
   *  this id".
   *
   *  Style: yellow (`CONSTRUCTION_STROKE()`) with a slightly larger
   *  dash than the line stroke so the frame reads as a separate
   *  object rather than a continuation of the line itself. The
   *  matching halo path sits underneath so the frame stays readable
   *  on the light-theme canvas and warm leather fill — same
   *  rationale as `buildConstructionLineHalo` (issue #70).
   *
   *  There's no visible centre marker — the bbox centre is still
   *  used as a snap point during a bbox-grabbed drag, but a rendered
   *  dot would compete with anchor handles for visual real estate.
   *  Both pieces sit in a Group tagged with `data.constructionGroupId`
   *  so the per-frame group-drag code can find and translate the
   *  overlay in lock-step with the members (without it, the bbox
   *  would only catch up on the next full render — same lag
   *  artefact as issue #85). */
  buildConstructionGroupOverlay(groupId: string): paper.Item | null {
    const project = this.host.getProject();
    if (!project) return null;
    const lineIds = descendantConstructionLineIds(project, groupId);
    const imageIds = descendantConstructionImageIds(project, groupId);
    if (lineIds.length === 0 && imageIds.length === 0) return null;
    const groupModel = projectConstructionGroups(project).find(
      (g) => g.id === groupId,
    );
    // Member tracing images contribute their placed rectangles to the
    // bbox (issue #509). Images don't rotate, so this union is exact
    // for the common un-rotated group and an accepted approximation
    // for a rotated group that also holds images.
    let imageUnion: paper.Rectangle | null = null;
    if (imageIds.length > 0) {
      const imageSet = new Set(imageIds);
      for (const img of projectTracingImages(project)) {
        if (!imageSet.has(img.id)) continue;
        const r = new this.scope.Rectangle(
          img.x,
          img.y,
          img.widthMm,
          img.heightMm,
        );
        imageUnion = imageUnion ? imageUnion.unite(r) : r;
      }
    }
    // Use the persistent un-rotated bbox (members un-rotated by
    // `-group.rotation` about the stored pivot, then unioned). Falls
    // back to the simple union of live rendered bounds for legacy
    // groups that don't carry rotation metadata yet.
    let unrotated: {
      x: number;
      y: number;
      width: number;
      height: number;
      cx: number;
      cy: number;
    } | null = null;
    if (groupModel && lineIds.length > 0) {
      unrotated = getUnrotatedGroupBounds(project, groupModel, this.scope);
    }
    if (!unrotated) {
      let union: paper.Rectangle | null = imageUnion;
      for (const lid of lineIds) {
        const item = this.host.getConstructionLineItem(lid);
        if (!item) continue;
        const b = item.bounds;
        union = union ? union.unite(b) : b;
      }
      if (!union) return null;
      unrotated = {
        x: union.x,
        y: union.y,
        width: union.width,
        height: union.height,
        cx: union.x + union.width / 2,
        cy: union.y + union.height / 2,
      };
    }
    // When `unrotated` came from `getUnrotatedGroupBounds` it already
    // folds member images in (the same way the rotation-handle hit-test
    // does), so the rendered frame + knob and the grabbable knob agree.
    // `imageUnion` is only needed for the fallback union above.
    const rect = new this.scope.Rectangle(
      unrotated.x,
      unrotated.y,
      unrotated.width,
      unrotated.height,
    );
    const rotation = groupModel?.rotation ?? 0;
    const pivot = new this.scope.Point(unrotated.cx, unrotated.cy);

    const dash: [number, number] = [6, 4];
    const frameStrokeWidth = 1.4;
    const haloColor = new this.scope.Color(CONSTRUCTION_HALO());
    haloColor.alpha = 0.6;
    const halo = new this.scope.Path.Rectangle({
      rectangle: rect,
      strokeColor: haloColor,
      strokeWidth: frameStrokeWidth + 1.6,
      strokeScaling: false,
      dashArray: dash,
    });
    halo.data = {
      kind: "construction-group-bbox-halo",
      constructionGroupId: groupId,
      ephemeral: true,
    };
    const frame = new this.scope.Path.Rectangle({
      rectangle: rect,
      strokeColor: new this.scope.Color(CONSTRUCTION_STROKE()),
      strokeWidth: frameStrokeWidth,
      strokeScaling: false,
      dashArray: dash,
    });
    frame.data = {
      kind: "construction-group-bbox",
      constructionGroupId: groupId,
    };
    const group = new this.scope.Group([halo, frame]);
    group.data = {
      kind: "construction-group-overlay",
      constructionGroupId: groupId,
    };
    if (this.host.constructionRotationHandlesEnabled()) {
      // Screen-constant group rotation knob + stem (#864) — same
      // zoom-invariant sizing as the per-line overlay.
      const zoom = this.scope.view?.zoom ?? 1;
      const topX = unrotated.x + unrotated.width / 2;
      const topY = unrotated.y;
      // Screen-constant stem offset — see buildLineRotationOverlay.
      const stemEnd = topY - this.host.lineRotationHandleOffsetMm() / zoom;
      const stem = new this.scope.Path.Line(
        new this.scope.Point(topX, topY),
        new this.scope.Point(topX, stemEnd),
      );
      stem.strokeColor = new this.scope.Color(SELECTION_STROKE());
      stem.strokeWidth = 1;
      stem.strokeScaling = false;
      stem.data = {
        kind: "group-rotation-stem",
        constructionGroupId: groupId,
        ephemeral: true,
      };
      const knob = new this.scope.Path.Circle(
        new this.scope.Point(topX, stemEnd),
        this.host.lineRotationHandleRadiusMm() / zoom,
      );
      knob.fillColor = new this.scope.Color(SELECTION_STROKE());
      knob.strokeColor = null;
      knob.strokeScaling = false;
      knob.data = {
        kind: "group-rotation-handle",
        constructionGroupId: groupId,
        ephemeral: true,
      };
      group.addChild(stem);
      group.addChild(knob);
      const hover = this.host.getGroupRotationHoverCorner();
      if (hover && hover.constructionGroupId === groupId) {
        const arc = this.buildRotationCornerArc(
          rect,
          hover.corner,
          `group:${groupId}`,
        );
        if (arc) group.addChild(arc);
      }
    }
    // Rotate the whole overlay by the persistent group rotation so
    // the bbox + handle + corner arcs render as a tilted rectangle
    // around the rotated cluster instead of a fresh axis-aligned
    // bbox.
    if (Math.abs(rotation) > 1e-6) {
      group.rotate(rotation, pivot);
    }
    return group;
  }

  /** Render one ray into `group`: the drawn line (dashed + origin dot +
   *  arrowhead), plus — when an angle is given — a geometry-style angle marker
   *  (a horizontal base ray, an arc between it and the line, and the degree
   *  value beside the arc). A length / radius value sits at the line's midpoint. */
  renderGuideRay(group: paper.Group, r: GuideRay): void {
    const dx = r.to.x - r.from.x;
    const dy = r.to.y - r.from.y;
    const length = Math.hypot(dx, dy);
    if (length < 1e-6) return;
    const ux = dx / length;
    const uy = dy / length;
    const zoom = this.scope.view.zoom || 1;
    // Over a leather piece the pale fill washes out the light accent — switch to
    // the darker leather accent (WCAG-contrasting). Test the ray's origin, so a
    // construction ray straying onto leather darkens while a leather-shape drag
    // out on the bare mat keeps the light accent.
    const color = new this.scope.Color(
      (r.overLeather ?? this.host.isPointOverLeather(r.from))
        ? LEATHER_SELECTION_STROKE()
        : SELECTION_STROKE(),
    );
    const P = this.scope.Point;

    const line = new this.scope.Path.Line(
      new P(r.from.x, r.from.y),
      new P(r.to.x, r.to.y),
    );
    line.strokeColor = color;
    line.strokeWidth = 1;
    line.strokeScaling = false;
    line.dashArray = [4, 3];
    // Tagged so tests / callers can count the number of rays drawn.
    line.data = { ephemeral: true, kind: "guide-ray-line" };
    group.addChild(line);

    const dot = new this.scope.Path.Circle(new P(r.from.x, r.from.y), 3 / zoom);
    dot.fillColor = color;
    dot.data = { ephemeral: true };
    group.addChild(dot);

    // Arrowhead at the endpoint, sized in screen px so it's zoom-constant.
    const px = -uy;
    const py = ux;
    const ah = 11 / zoom;
    const aw = 5 / zoom;
    const backX = r.to.x - ux * ah;
    const backY = r.to.y - uy * ah;
    const head = new this.scope.Path([
      new P(r.to.x, r.to.y),
      new P(backX + px * aw, backY + py * aw),
      new P(backX - px * aw, backY - py * aw),
    ]);
    head.closed = true;
    head.fillColor = color;
    head.data = { ephemeral: true };
    group.addChild(head);

    // Geometry-style angle marker at the vertex (angle from the horizontal).
    // Labels placed earlier become obstacles for the later ones (#938): the
    // angle value anchors its wedge, the corner value escapes along its own
    // bisector, and the measure label both scores against them and nudges
    // clear as a fallback.
    const placedLabels: paper.PointText[] = [];
    // Single-marker rays (#938): "corner" suppresses the horizontal marker,
    // "horizontal" suppresses the corner. Absent = the dual display.
    const showHorizontal = r.angleDeg != null && r.angleRef !== "corner";
    const showCorner = r.cornerPrev != null && r.angleRef !== "horizontal";
    if (showHorizontal && r.angleDeg != null) {
      // When the corner's incoming edge lies ON the horizontal base ray
      // (the previous segment was drawn right-to-left), the corner angle
      // and the horizontal angle are the same number — showing it twice is
      // useless. Flip the base to the 180° side so the marker reads the
      // supplementary angle instead (#938).
      let flipBase = false;
      if (r.cornerPrev) {
        const pdx = r.cornerPrev.x - r.from.x;
        const pdy = r.cornerPrev.y - r.from.y;
        if (
          Math.hypot(pdx, pdy) > 1e-6 &&
          pdx > 0 &&
          Math.abs(Math.atan2(pdy, pdx)) < 0.014 // ≈ 0.8°
        ) {
          flipBase = true;
        }
      }
      const angleLabel = this.renderAngleMarker(
        group,
        r.from,
        length,
        r.angleDeg,
        color,
        zoom,
        r.angleStepDeg,
        flipBase,
      );
      if (angleLabel) placedLabels.push(angleLabel);
    }
    // Interior-angle arc between the previous polyline / polygon edge and this
    // segment (#747), drawn just outside the horizontal-angle marker.
    if (showCorner && r.cornerPrev != null) {
      const corner = this.renderCornerAngle(
        group,
        r.from,
        r.cornerPrev,
        r.to,
        color,
        zoom,
        r.cornerPrevGhost === true,
        r.angleStepDeg,
      );
      if (corner) {
        this.nudgeClearOfLabels(
          corner.label,
          placedLabels,
          corner.dirX,
          corner.dirY,
          zoom,
        );
        placedLabels.push(corner.label);
      }
    }

    // Length / radius value at the line's midpoint (offset to one side) —
    // nudged so the text neither crosses its own ray (long labels next to a
    // diagonal segment used to clip it) nor sits over a leather part when
    // the mirrored side is clear (#938).
    const measure = this.guideMeasureLabel(r);
    if (measure) {
      try {
        const midX = (r.from.x + r.to.x) / 2;
        const midY = (r.from.y + r.to.y) / 2;
        const label = new this.scope.PointText({
          point: new P(midX + px * (14 / zoom), midY + py * (14 / zoom)),
          content: measure,
          fillColor: color,
          fontFamily: "sans-serif",
          fontSize: 12 / zoom,
          fontWeight: 700,
          justification: "center",
        });
        label.data = { ephemeral: true, kind: "measure-label" };
        this.placeClearOfRayAndLeather(
          label,
          r.from,
          r.to,
          { x: midX, y: midY },
          px,
          py,
          14 / zoom,
          zoom,
          placedLabels,
        );
        group.addChild(label);
      } catch {
        /* text unavailable in headless — line + arrow still render */
      }
    }
  }

  /** Segment ↔ rectangle overlap test for label placement: true when the
   *  segment `a`→`b` touches `rect` (an endpoint inside, or the segment
   *  crossing any rect edge). */
  private segmentIntersectsRect(
    a: { x: number; y: number },
    b: { x: number; y: number },
    rect: paper.Rectangle,
  ): boolean {
    const inside = (p: { x: number; y: number }) =>
      p.x >= rect.left &&
      p.x <= rect.right &&
      p.y >= rect.top &&
      p.y <= rect.bottom;
    if (inside(a) || inside(b)) return true;
    const cross = (
      p: { x: number; y: number },
      q: { x: number; y: number },
      r2: { x: number; y: number },
    ) => (q.x - p.x) * (r2.y - p.y) - (q.y - p.y) * (r2.x - p.x);
    const segsIntersect = (
      p1: { x: number; y: number },
      p2: { x: number; y: number },
      p3: { x: number; y: number },
      p4: { x: number; y: number },
    ) => {
      const d1 = cross(p3, p4, p1);
      const d2 = cross(p3, p4, p2);
      const d3 = cross(p1, p2, p3);
      const d4 = cross(p1, p2, p4);
      return (
        ((d1 > 0 && d2 < 0) || (d1 < 0 && d2 > 0)) &&
        ((d3 > 0 && d4 < 0) || (d3 < 0 && d4 > 0))
      );
    };
    const tl = { x: rect.left, y: rect.top };
    const tr = { x: rect.right, y: rect.top };
    const br = { x: rect.right, y: rect.bottom };
    const bl = { x: rect.left, y: rect.bottom };
    return (
      segsIntersect(a, b, tl, tr) ||
      segsIntersect(a, b, tr, br) ||
      segsIntersect(a, b, br, bl) ||
      segsIntersect(a, b, bl, tl)
    );
  }

  /** Nudge a freshly created label to a clear spot (#938): try the default
   *  side of the ray, the mirrored side, then both again farther out, and
   *  keep the first (best) candidate whose text box neither crosses the
   *  labelled segment nor sits over a leather part. Crossing the ray weighs
   *  more than covering leather; the default side wins ties. Best effort —
   *  with every candidate dirty (e.g. a seam deep inside a part), the
   *  default placement stands. */
  private placeClearOfRayAndLeather(
    label: paper.PointText,
    from: { x: number; y: number },
    to: { x: number; y: number },
    mid: { x: number; y: number },
    px: number,
    py: number,
    baseOff: number,
    zoom: number,
    obstacles: paper.PointText[] = [],
  ): void {
    const pad = 3 / zoom;
    let bestPos: paper.Point | null = null;
    let bestOff = baseOff;
    let bestScore = Infinity;
    for (const off of [baseOff, -baseOff, 2.6 * baseOff, -2.6 * baseOff]) {
      label.position = new this.scope.Point(mid.x + px * off, mid.y + py * off);
      const bounds = label.bounds.expand(pad * 2);
      // Overlapping another ray label (the angle / corner value) is the
      // worst outcome — it makes both unreadable (#938).
      const overlapsLabel = obstacles.some((o) => bounds.intersects(o.bounds))
        ? 4
        : 0;
      const crossesRay = this.segmentIntersectsRect(from, to, bounds) ? 2 : 0;
      const overLeather = this.host.isPointOverLeather({
        x: label.position.x,
        y: label.position.y,
      })
        ? 1
        : 0;
      const score = overlapsLabel + crossesRay + overLeather;
      if (score < bestScore) {
        bestScore = score;
        bestPos = label.position.clone();
        bestOff = off;
        if (score === 0) break;
      }
    }
    if (bestPos) label.position = bestPos;
    // Fallback: every candidate collided with another label — slide farther
    // along the chosen side until clear.
    this.nudgeClearOfLabels(
      label,
      obstacles,
      px * Math.sign(bestOff),
      py * Math.sign(bestOff),
      zoom,
    );
  }

  /** Slide `label` along (dirX, dirY) in small steps until it no longer
   *  overlaps any of `others` (bounded — gives up rather than flying off). */
  private nudgeClearOfLabels(
    label: paper.PointText,
    others: paper.PointText[],
    dirX: number,
    dirY: number,
    zoom: number,
  ): void {
    if (!others.length) return;
    const norm = Math.hypot(dirX, dirY);
    if (norm < 1e-9) return;
    const step = 8 / zoom;
    const sx = (dirX / norm) * step;
    const sy = (dirY / norm) * step;
    for (let i = 0; i < 8; i++) {
      const b = label.bounds.expand((3 / zoom) * 2);
      if (!others.some((o) => b.intersects(o.bounds))) return;
      label.position = label.position.add(new this.scope.Point(sx, sy));
    }
  }

  /** Place a wedge label (the horizontal-angle / corner value) at the
   *  nearest clear spot around its arc (#938). Candidates walk outward
   *  along the bisector AND sideways off it — the wedge edges are finite,
   *  so a narrow angle's value can often sit just past an edge (outside the
   *  wedge) instead of being pushed far along the bisector. A spot is
   *  clear when the text box misses both edges and the arc itself. Nothing
   *  clear → the farthest bisector spot. */
  private placeWedgeLabel(
    label: paper.PointText,
    vertex: { x: number; y: number },
    edgeA: { x: number; y: number },
    edgeB: { x: number; y: number },
    bis: number,
    startR: number,
    arcPts: { x: number; y: number }[],
    zoom: number,
  ): void {
    const P = this.scope.Point;
    const pad = 3 / zoom;
    const bx = Math.cos(bis);
    const by = Math.sin(bis);
    // Lateral (perpendicular-to-bisector) step ≈ the text height.
    const lat = Math.max(label.bounds.height, 14 / zoom);
    const radii = [startR, 1.4 * startR, 1.9 * startR, 2.6 * startR];
    const laterals = [0, lat, -lat, 2 * lat, -2 * lat];
    for (const r of radii) {
      for (const t of laterals) {
        label.position = new P(
          vertex.x + bx * r - by * t,
          vertex.y + by * r + bx * t,
        );
        const b = label.bounds.expand(pad * 2);
        const hitsEdge =
          this.segmentIntersectsRect(vertex, edgeA, b) ||
          this.segmentIntersectsRect(vertex, edgeB, b);
        const hitsArc = arcPts.some(
          (p) =>
            p.x >= b.left && p.x <= b.right && p.y >= b.top && p.y <= b.bottom,
        );
        if (!hitsEdge && !hitsArc) return;
      }
    }
    const rMax = radii[radii.length - 1]!;
    label.position = new P(vertex.x + bx * rMax, vertex.y + by * rMax);
  }

  /** Sample a marker arc (radius `r` around `vertex`, screen angles `a0` →
   *  `a1`) for the wedge label's overlap test. */
  private sampleArc(
    vertex: { x: number; y: number },
    r: number,
    a0: number,
    a1: number,
  ): { x: number; y: number }[] {
    const pts: { x: number; y: number }[] = [];
    const N = 9;
    for (let i = 0; i <= N; i++) {
      const a = a0 + ((a1 - a0) * i) / N;
      pts.push({
        x: vertex.x + r * Math.cos(a),
        y: vertex.y + r * Math.sin(a),
      });
    }
    return pts;
  }

  /** Compose a ray's NON-angle value label (length / radius / explicit text).
   *  The angle, when present, is shown by the geometry angle marker instead. */
  private guideMeasureLabel(r: GuideRay): string {
    if (r.text != null) return r.text;
    const parts: string[] = [];
    if (r.lengthMm != null)
      parts.push(this.host.formatDragDistance(r.lengthMm));
    if (r.radiusMm != null)
      parts.push("R " + this.host.formatDragDistance(r.radiusMm));
    return parts.join("   ");
  }

  /** Draw a geometry-style angle marker at `from`: a horizontal base ray (the
   *  reference "base" of the angle), an arc between it and the drawn line, and
   *  the angle value next to the arc. When the angle is 90° a small right-angle
   *  square replaces the arc. The value is the (non-reflex) angle between the
   *  horizontal and the line — 0…180°. `sideLen` is the drawn line's length, so
   *  the base ray matches it and the marker reads as a proper angle. */
  /** " (72° × 2)" when `valDeg` sits on a ≥ 2× multiple of the preset step
   *  (#937) — annotating the math tells a step multiple (144 = 72 × 2) apart
   *  from a complementary value (108 = 180 − 72) at a glance. Single
   *  multiples (the step itself) stay bare — they already read as the step. */
  private stepMultipleSuffix(valDeg: number, stepDeg?: number): string {
    if (stepDeg == null || stepDeg <= 0) return "";
    const k = Math.round(valDeg / stepDeg);
    if (k < 2 || Math.abs(valDeg - k * stepDeg) > 0.05) return "";
    const step = Math.round(stepDeg * 10) / 10;
    return ` (${step}° × ${k})`;
  }

  private renderAngleMarker(
    group: paper.Group,
    from: { x: number; y: number },
    sideLen: number,
    angleDeg: number,
    color: paper.Color,
    zoom: number,
    angleStepDeg?: number,
    flipBase = false,
  ): paper.PointText | null {
    const P = this.scope.Point;
    const theta = ((((angleDeg % 360) + 360) % 360) * Math.PI) / 180; // y-up
    // Base reference: the +x horizontal, or the −x side when the +x reading
    // would duplicate the corner label (#938).
    const baseAng = flipBase ? Math.PI : 0;
    const bxu = Math.cos(baseAng);
    const byu = Math.sin(baseAng);
    // Signed screen angle from the base ray to the drawn line, folded to
    // (-π, π] so the arc always spans the non-reflex angle (≤ 180°).
    let sAng = -theta - baseAng;
    while (sAng <= -Math.PI) sAng += 2 * Math.PI;
    while (sAng > Math.PI) sAng -= 2 * Math.PI;
    const phiDeg = Math.abs((sAng * 180) / Math.PI); // 0..180
    // #744: don't show a 0° angle marker (the line is already on the horizontal;
    // a base ray + "0°" adds nothing).
    if (Math.round(phiDeg) === 0) return null;
    const bis = baseAng + sAng / 2; // bisector screen angle

    // Base ray — the reference from the vertex, matching the line length.
    const base = new this.scope.Path.Line(
      new P(from.x, from.y),
      new P(from.x + sideLen * bxu, from.y + sideLen * byu),
    );
    base.strokeColor = color;
    base.strokeWidth = 1;
    base.strokeScaling = false;
    base.dashArray = [4, 3];
    base.opacity = 0.55;
    base.data = { ephemeral: true, kind: "angle-base" };
    group.addChild(base);

    const near90 = Math.abs(phiDeg - 90) < 0.5;
    // Near 0° / 180° there's no meaningful corner to mark — just show the value.
    const degenerate = phiDeg < 1 || phiDeg > 179;
    let markerR = 8 / zoom;
    if (!degenerate && near90) {
      // Right-angle square: two short sides along the base and the line.
      const s = Math.min(16 / zoom, 0.42 * sideLen);
      markerR = s;
      const sx = Math.cos(baseAng + sAng);
      const sy = Math.sin(baseAng + sAng);
      const square = new this.scope.Path([
        new P(from.x + s * bxu, from.y + s * byu),
        new P(from.x + s * bxu + s * sx, from.y + s * byu + s * sy),
        new P(from.x + s * sx, from.y + s * sy),
      ]);
      square.strokeColor = color;
      square.strokeWidth = 1;
      square.strokeScaling = false;
      square.fillColor = null;
      square.data = { ephemeral: true, kind: "angle-square" };
      group.addChild(square);
    } else if (!degenerate) {
      // Arc from the base ray to the line.
      const R = Math.min(22 / zoom, 0.45 * sideLen);
      markerR = R;
      try {
        const arc = new this.scope.Path.Arc(
          new P(from.x + R * bxu, from.y + R * byu),
          new P(from.x + R * Math.cos(bis), from.y + R * Math.sin(bis)),
          new P(
            from.x + R * Math.cos(baseAng + sAng),
            from.y + R * Math.sin(baseAng + sAng),
          ),
        );
        arc.strokeColor = color;
        arc.strokeWidth = 1;
        arc.strokeScaling = false;
        arc.fillColor = null;
        arc.data = { ephemeral: true, kind: "angle-arc" };
        group.addChild(arc);
      } catch {
        /* near-degenerate arc — the value label still conveys the angle */
      }
    }

    // Angle value, sat just outside the marker along the bisector — pushed
    // farther out when the wedge is too narrow for the text (#938).
    try {
      const lr = markerR + 13 / zoom;
      const label = new this.scope.PointText({
        point: new P(from.x + lr * Math.cos(bis), from.y + lr * Math.sin(bis)),
        content: `${Math.round(phiDeg)}°${this.stepMultipleSuffix(phiDeg, angleStepDeg)}`,
        fillColor: color,
        fontFamily: "sans-serif",
        fontSize: 12 / zoom,
        fontWeight: 700,
        justification: "center",
      });
      label.data = { ephemeral: true, kind: "angle-label" };
      this.placeWedgeLabel(
        label,
        from,
        { x: from.x + sideLen * bxu, y: from.y + sideLen * byu },
        {
          x: from.x + sideLen * Math.cos(baseAng + sAng),
          y: from.y + sideLen * Math.sin(baseAng + sAng),
        },
        bis,
        lr,
        this.sampleArc(from, markerR, baseAng, baseAng + sAng),
        zoom,
      );
      group.addChild(label);
      return label;
    } catch {
      /* text unavailable in headless */
    }
    return null;
  }

  /** Draw the INTERIOR angle at `vertex` between the incoming polyline edge
   *  (`vertex`→`prev`) and the current segment (`vertex`→`to`) — an arc (or a
   *  right-angle square at 90°) with the angle value. Sat at a larger radius than
   *  the horizontal-angle marker so the two don't overlap (#747). */
  private renderCornerAngle(
    vertexGroup: paper.Group,
    vertex: { x: number; y: number },
    prev: { x: number; y: number },
    to: { x: number; y: number },
    color: paper.Color,
    zoom: number,
    ghostPrev = false,
    angleStepDeg?: number,
  ): { label: paper.PointText; dirX: number; dirY: number } | null {
    const P = this.scope.Point;
    const inLen = Math.hypot(prev.x - vertex.x, prev.y - vertex.y);
    const outLen = Math.hypot(to.x - vertex.x, to.y - vertex.y);
    if (inLen < 1e-6 || outLen < 1e-6) return null;
    // Unit directions along the two edges, away from the shared vertex.
    const ax = (prev.x - vertex.x) / inLen;
    const ay = (prev.y - vertex.y) / inLen;
    const bx = (to.x - vertex.x) / outLen;
    const by = (to.y - vertex.y) / outLen;
    const aAng = Math.atan2(ay, ax);
    let sweep = Math.atan2(by, bx) - aAng;
    while (sweep <= -Math.PI) sweep += 2 * Math.PI;
    while (sweep > Math.PI) sweep -= 2 * Math.PI;
    const phiDeg = Math.abs((sweep * 180) / Math.PI); // 0..180 interior angle
    // #744: don't show a 0° angle marker (the two edges point the same way —
    // nothing to indicate). Skip the ghost too.
    if (Math.round(phiDeg) === 0) return null;
    // Ghost ray for the OLD segment (vertex → prev), so the arc has a line on
    // both sides when the previous edge isn't otherwise drawn (a point move).
    if (ghostPrev) {
      const ghost = new this.scope.Path.Line(
        new P(vertex.x, vertex.y),
        new P(prev.x, prev.y),
      );
      ghost.strokeColor = color;
      ghost.strokeWidth = 1;
      ghost.strokeScaling = false;
      ghost.dashArray = [4, 3];
      ghost.opacity = 0.55;
      ghost.data = { ephemeral: true, kind: "corner-ghost" };
      vertexGroup.addChild(ghost);
    }
    const degenerate = phiDeg < 1 || phiDeg > 179;
    const mid = aAng + sweep / 2;
    const near90 = Math.abs(phiDeg - 90) < 0.5;
    // Larger than the horizontal marker's radius (≤ 22px) so they nest.
    let markerR = 34 / zoom;
    if (!degenerate && near90) {
      const s = Math.min(18 / zoom, 0.45 * Math.min(inLen, outLen));
      markerR = s;
      const square = new this.scope.Path([
        new P(vertex.x + s * ax, vertex.y + s * ay),
        new P(vertex.x + s * (ax + bx), vertex.y + s * (ay + by)),
        new P(vertex.x + s * bx, vertex.y + s * by),
      ]);
      square.strokeColor = color;
      square.strokeWidth = 1;
      square.strokeScaling = false;
      square.fillColor = null;
      square.data = { ephemeral: true, kind: "corner-square" };
      vertexGroup.addChild(square);
    } else if (!degenerate) {
      const R = Math.min(34 / zoom, 0.6 * Math.min(inLen, outLen));
      markerR = R;
      try {
        const arc = new this.scope.Path.Arc(
          new P(vertex.x + R * Math.cos(aAng), vertex.y + R * Math.sin(aAng)),
          new P(vertex.x + R * Math.cos(mid), vertex.y + R * Math.sin(mid)),
          new P(
            vertex.x + R * Math.cos(aAng + sweep),
            vertex.y + R * Math.sin(aAng + sweep),
          ),
        );
        arc.strokeColor = color;
        arc.strokeWidth = 1;
        arc.strokeScaling = false;
        arc.fillColor = null;
        arc.data = { ephemeral: true, kind: "corner-arc" };
        vertexGroup.addChild(arc);
      } catch {
        /* near-degenerate — the value label still conveys the angle */
      }
    }
    try {
      const lr = markerR + 13 / zoom;
      const label = new this.scope.PointText({
        point: new P(
          vertex.x + lr * Math.cos(mid),
          vertex.y + lr * Math.sin(mid),
        ),
        content: `${Math.round(phiDeg)}°${this.stepMultipleSuffix(phiDeg, angleStepDeg)}`,
        fillColor: color,
        fontFamily: "sans-serif",
        fontSize: 12 / zoom,
        fontWeight: 700,
        justification: "center",
      });
      label.data = { ephemeral: true, kind: "corner-label" };
      // Sharp corners squeeze the bisector spot between the two edges —
      // find the nearest clear spot around the arc instead (#938).
      this.placeWedgeLabel(
        label,
        vertex,
        prev,
        to,
        mid,
        lr,
        this.sampleArc(vertex, markerR, aAng, aAng + sweep),
        zoom,
      );
      vertexGroup.addChild(label);
      return { label, dirX: Math.cos(mid), dirY: Math.sin(mid) };
    } catch {
      /* text unavailable in headless */
    }
    return null;
  }
}

/** Build the anchor lock-mode map (`pathIndex:segmentIndex` → "angle" |
 *  "smooth") for a construction line's smooth anchors. Shared by the handle
 *  builders here and Scene's transient handle refresh. */
export function buildAnchorLockMap(
  line: { smoothAnchors?: ConstructionLine["smoothAnchors"] } | undefined,
): Map<string, "angle" | "smooth"> {
  const map = new Map<string, "angle" | "smooth">();
  for (const a of line?.smoothAnchors ?? []) {
    map.set(`${a.pathIndex}:${a.segmentIndex}`, a.mode ?? "smooth");
  }
  return map;
}
